if (!customElements.get('shipping-info-tooltip')) {
  customElements.define('shipping-info-tooltip', class extends HTMLElement {
    get button() { return this.querySelector('.shipping-info__tooltip-btn'); }

    // Re-checked live (rather than cached once) so a PDP variant swap,
    // which replaces this element's inner HTML with a fresh panel, still
    // gets picked up and re-parented.
    get panel() {
      const fresh = this.querySelector('.shipping-info__tooltip-panel');
      if (fresh && fresh !== this._panel) {
        // Move it out to <body>: position: fixed only measures correctly
        // against the viewport when nothing between the panel and <body>
        // applies a transform. Drawers translate their whole panel to
        // slide in/out, which turns position: fixed into "fixed relative
        // to that transformed ancestor" instead, silently mispositioning
        // (or clipping, via the drawer's own overflow: hidden) the
        // tooltip. Re-parenting sidesteps that entirely.
        if (this._panel) this._panel.remove();
        document.body.appendChild(fresh);
        this._panel = fresh;
      }
      return this._panel;
    }

    // Listener wiring lives in document-level delegation below rather than
    // connectedCallback: in this theme, cart/section re-renders can produce
    // <shipping-info-tooltip> instances whose connectedCallback never fires
    // (confirmed - button/panel present, but usesPopover stays undefined
    // until this is called manually), so per-instance lifecycle hooks
    // aren't reliable here. This only needs the element to exist in the
    // DOM, not to have been "upgraded" in time.
    ready() {
      if (this._ready) return true;
      if (!this.button || !this.panel) return false;
      this._ready = true;

      this.usesPopover = typeof this.panel.showPopover === 'function';
      if (!this.usesPopover) {
        this.panel.removeAttribute('popover');
      }

      this.show = this.show.bind(this);
      this.hide = this.hide.bind(this);
      this.reposition = this.reposition.bind(this);
      this.onDocumentPointer = this.onDocumentPointer.bind(this);
      return true;
    }

    disconnectedCallback() {
      // Close first: hide() strips the window/document listeners show()
      // registered, so a detached host doesn't keep receiving reposition
      // calls or leak the pointerdown listener.
      this.hide();
      // Don't leak the body-appended panel once the trigger (and any cart
      // update's fresh replacement of it) is gone.
      if (this._panel) {
        this._panel.remove();
        this._panel = null;
      }
    }

    get isOpen() {
      if (!this.ready()) return false;
      if (this.usesPopover) {
        try { return this.panel.matches(':popover-open'); } catch (e) { return false; }
      }
      return this._open === true;
    }

    toggle() {
      if (this.isOpen) this.hide(); else this.show();
    }

    onDocumentPointer(e) {
      if (!this.contains(e.target) && e.target !== this.panel && !this.panel.contains(e.target)) this.hide();
    }

    show() {
      if (!this.ready()) return;
      if (this.usesPopover) {
        try { this.panel.showPopover(); } catch (e) { /* already open */ }
      } else {
        this._open = true;
        this.panel.classList.add('shipping-info__tooltip-panel--open');
      }
      this.reposition();
      window.addEventListener('scroll', this.reposition, true);
      window.addEventListener('resize', this.reposition);
      // Tap anywhere outside closes it on touch, where blur/mouseleave don't fire.
      document.addEventListener('pointerdown', this.onDocumentPointer);
    }

    hide() {
      if (!this.ready()) return;
      if (this.usesPopover) {
        try { this.panel.hidePopover(); } catch (e) { /* already closed */ }
      } else {
        this._open = false;
        this.panel.classList.remove('shipping-info__tooltip-panel--open');
      }
      window.removeEventListener('scroll', this.reposition, true);
      window.removeEventListener('resize', this.reposition);
      document.removeEventListener('pointerdown', this.onDocumentPointer);
    }

    reposition() {
      if (!this.ready()) return;
      const hostRect = this.getBoundingClientRect();

      // Match the trigger's width and align/center on the component itself.
      // Extend the panel past the component's right edge so the arrow, which
      // sits at the icon on the right, isn't jammed against the panel edge.
      const overflow = 24;
      this.panel.style.width = (hostRect.width + overflow) + 'px';
      this.panel.style.left = hostRect.left + 'px';
      this.panel.style.top = (hostRect.bottom + 12) + 'px';
      // Arrow points at the info icon, relative to the panel's left edge.
      const btnRect = this.button.getBoundingClientRect();
      const arrowX = btnRect.left + (btnRect.width / 2) - hostRect.left;
      this.panel.style.setProperty('--arrow-x', arrowX + 'px');
    }
  });
}

// Document-level delegation: see the comment on ready() above for why this
// isn't done via connectedCallback + per-instance addEventListener calls.
// Registered once, independent of any particular <shipping-info-tooltip>
// instance's lifecycle.
if (!window.__shippingInfoTooltipDelegated) {
  window.__shippingInfoTooltipDelegated = true;

  const closestTrigger = (node) => (node && node.closest ? node.closest('shipping-info-tooltip') : null);
  let lastPointerType = null;

  document.addEventListener('pointerdown', (e) => { lastPointerType = e.pointerType; }, true);

  // pointerover/pointerout bubble (unlike pointerenter/pointerleave), so a
  // single document listener can catch them; relatedTarget is checked to
  // only react when the pointer actually crosses the component's boundary,
  // not on every move between its child elements.
  document.addEventListener('pointerover', (e) => {
    if (e.pointerType !== 'mouse') return;
    const trigger = closestTrigger(e.target);
    if (!trigger || trigger.contains(e.relatedTarget)) return;
    trigger.show();
  });

  document.addEventListener('pointerout', (e) => {
    if (e.pointerType !== 'mouse') return;
    const trigger = closestTrigger(e.target);
    if (!trigger || trigger.contains(e.relatedTarget)) return;
    trigger.hide();
  });

  document.addEventListener('focusin', (e) => {
    const trigger = closestTrigger(e.target);
    if (trigger) trigger.show();
  });

  document.addEventListener('focusout', (e) => {
    const trigger = closestTrigger(e.target);
    if (trigger) trigger.hide();
  });

  // Only toggle on click for touch/pen: for a mouse, pointerover has
  // already opened it by the time click fires, so toggling here too would
  // immediately close it again.
  document.addEventListener('click', (e) => {
    const trigger = closestTrigger(e.target);
    if (!trigger || lastPointerType === 'mouse') return;
    e.stopPropagation();
    trigger.toggle();
  });
}
